import type {
  WeeklyReimbursementsMetricCardProps,
  WeeklyReimbursementsSummaryOverviewProps,
} from '../types/components'
import {
  BarChart3,
  Calendar,
  Coins,
  DollarSign,
  FileText,
  LineChart,
  Percent,
  Receipt,
  RotateCcw,
  Users,
  Wallet,
} from 'lucide-react'
import { formatAmount } from '@/lib/utils/formatters'

function pct(value: string | number | undefined): string {
  if (value == null || value === '') return '0%'
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(n)) return '0%'
  return `${n.toFixed(2)}%`
}

function MetricCard({ label, value, icon, iconBgClass, testId }: WeeklyReimbursementsMetricCardProps) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col justify-between rounded-xl border border-slate-200/80 bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all hover:border-slate-300 hover:shadow-sm"
    >
      <div className="flex items-center gap-2">
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${iconBgClass}`}
        >
          {icon}
        </div>
        <span className="text-[11px] font-medium leading-tight text-slate-600">
          {label}
        </span>
      </div>
      <div className="mt-3 text-sm font-bold tracking-tight text-slate-900 sm:text-base">
        {value}
      </div>
    </div>
  )
}

export function WeeklyReimbursementsSummaryOverview({
  totalCount: tc,
  reportData,
  loading = false,
}: WeeklyReimbursementsSummaryOverviewProps) {
  return (
    <div
      data-testid="weekly-reimbursements-summary-overview"
      className={`rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition-opacity duration-200 sm:p-6 ${
        loading ? 'opacity-60 pointer-events-none' : 'opacity-100'
      }`}
    >
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgb(21_151_255)]/10 text-[rgb(21_151_255)]">
          <LineChart className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">
            Revenue Overview
          </h2>
          <p className="text-xs text-slate-500">
            Summary of revenue, premiums, reimbursements and pending amounts
          </p>
        </div>
      </div>

      {/* Section 1: Revenue & Reimbursements */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-1 rounded-full bg-[rgb(21_151_255)]" />
          <h3 className="text-xs font-bold tracking-wide text-[rgb(21_151_255)]">
            Revenue & Reimbursements
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9">
          <MetricCard
            label="Total Revenue"
            value={formatAmount(tc.total_revenue)}
            icon={<DollarSign className="h-3.5 w-3.5 text-blue-600" />}
            iconBgClass="bg-blue-50"
            testId="metric-total-revenue"
          />
          <MetricCard
            label="Total MRG Premium Paid"
            value={formatAmount(tc.total_mrg_premium)}
            icon={<FileText className="h-3.5 w-3.5 text-emerald-600" />}
            iconBgClass="bg-emerald-50"
            testId="metric-total-mrg-premium"
          />
          <MetricCard
            label="# Reimb. Paid"
            value={tc.total_no_claim_paid ?? '0'}
            icon={<Receipt className="h-3.5 w-3.5 text-purple-600" />}
            iconBgClass="bg-purple-50"
            testId="metric-total-claims-paid-count"
          />
          <MetricCard
            label="Reimb. Paid (Actual)"
            value={formatAmount(tc.total_claim_paid)}
            icon={<RotateCcw className="h-3.5 w-3.5 text-amber-600" />}
            iconBgClass="bg-amber-50"
            testId="metric-total-claim-paid-amt"
          />
          <MetricCard
            label="(Paid / Premium) %"
            value={pct(tc.percent_of_claim_premium)}
            icon={<Percent className="h-3.5 w-3.5 text-teal-600" />}
            iconBgClass="bg-teal-50"
            testId="metric-paid-premium-pct"
          />
          <MetricCard
            label="Paid & Pending Count"
            value={tc.total_paid_n_pending_claims_count ?? '0'}
            icon={<Users className="h-3.5 w-3.5 text-blue-600" />}
            iconBgClass="bg-blue-50"
            testId="metric-paid-pending-count"
          />
          <MetricCard
            label="Paid & Pending Amt"
            value={formatAmount(tc.total_paid_n_pending_claims_amount)}
            icon={<Coins className="h-3.5 w-3.5 text-amber-500" />}
            iconBgClass="bg-amber-50"
            testId="metric-paid-pending-amt"
          />
          <MetricCard
            label="Premium %"
            value={
              reportData?.mrg_premium_percent != null
                ? `${reportData.mrg_premium_percent}%`
                : '—'
            }
            icon={<Percent className="h-3.5 w-3.5 text-purple-600" />}
            iconBgClass="bg-purple-50"
            testId="metric-premium-pct"
          />
          <MetricCard
            label="Avg Reimb. Paid"
            value={formatAmount(tc.average_claim_paid)}
            icon={<BarChart3 className="h-3.5 w-3.5 text-sky-600" />}
            iconBgClass="bg-sky-50"
            testId="metric-avg-claim-paid"
          />
        </div>
      </div>

      {/* Section 2: Pending & Remaining */}
      <div className="mt-6 space-y-3">
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-1 rounded-full bg-[rgb(21_151_255)]" />
          <h3 className="text-xs font-bold tracking-wide text-[rgb(21_151_255)]">
            Pending & Remaining
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
          <MetricCard
            label="Remaining Premium $"
            value={formatAmount(tc.remaining_mrg)}
            icon={<DollarSign className="h-3.5 w-3.5 text-emerald-600" />}
            iconBgClass="bg-emerald-50 border border-dashed border-emerald-300"
            testId="metric-remaining-premium-amt"
          />
          <MetricCard
            label="Remaining Premium %"
            value={pct(tc.remaining_mrg_percent)}
            icon={<Percent className="h-3.5 w-3.5 text-emerald-600" />}
            iconBgClass="bg-emerald-50 border border-dashed border-emerald-300"
            testId="metric-remaining-premium-pct"
          />
          <MetricCard
            label="Expected Remaining after Pending $"
            value={formatAmount(tc.expected_remaining_mrg)}
            icon={<DollarSign className="h-3.5 w-3.5 text-blue-600" />}
            iconBgClass="bg-blue-50 border border-dashed border-blue-300"
            testId="metric-expected-remaining-amt"
          />
          <MetricCard
            label="Expected Remaining after Pending %"
            value={pct(tc.expected_remaining_mrg_percent)}
            icon={<Percent className="h-3.5 w-3.5 text-blue-600" />}
            iconBgClass="bg-blue-50 border border-dashed border-blue-300"
            testId="metric-expected-remaining-pct"
          />
          <MetricCard
            label="Pending Reimb. Count"
            value={tc.total_pending_claims_count ?? '0'}
            icon={<Users className="h-3.5 w-3.5 text-purple-600" />}
            iconBgClass="bg-purple-50"
            testId="metric-pending-claims-count"
          />
          <MetricCard
            label="Pending Reimb. Amount"
            value={formatAmount(tc.total_pending_claims_amount)}
            icon={<Wallet className="h-3.5 w-3.5 text-amber-600" />}
            iconBgClass="bg-amber-50"
            testId="metric-pending-claims-amt"
          />
          <MetricCard
            label="Weeks w/ Zero Paid"
            value={tc.count_of_zero_claim_week ?? '0'}
            icon={<Calendar className="h-3.5 w-3.5 text-blue-600" />}
            iconBgClass="bg-blue-50"
            testId="metric-zero-paid-weeks"
          />
        </div>
      </div>
    </div>
  )
}
